import { useContext } from 'react';

import { Box } from '@/components/Box';
import { Price as BasePrice } from '@/components/Price';
import type { MoneyFragment } from '@/graphql/saleor/api';
import type { CURRENCY_DISPLAY } from '@/lib/intl';
import { generateDataTestId } from '@/lib/tests';
import type { Maybe } from '@/lib/types';
import { useCommonModalProviderContext } from '@/providers/CommonModalProvider';
import { RetailerPageContext } from '@/providers/RetailerPageProvider/RetailerPageProviderContext';

import infoBadge from '@/assets/icons/info-badge.svg';

import * as S from './Price.styles';

export interface PriceProps {
  children?: React.ReactNode;
  className?: string;
  formatPriceOpts?: {
    currencyDisplay?: CURRENCY_DISPLAY;
    maximumFractionDigits?: number;
  };
  onClick?: () => void;
  price: Maybe<MoneyFragment>;
  priceUndiscounted?: Maybe<MoneyFragment>;
  size?: 's' | 'm';
  sku?: string;
}

export const Price = ({
  className,
  formatPriceOpts,
  onClick,
  price,
  priceUndiscounted,
  size = 's',
}: PriceProps) => {
  const { setPriceModalStatus } = useCommonModalProviderContext();
  const retailerContext = useContext(RetailerPageContext);
  const onPriceClick = (evt: React.MouseEvent) => {
    evt.preventDefault();
    evt.stopPropagation();
    onClick?.();
  };

  const showPriceModal = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setPriceModalStatus(true);
  };

  return !price?.amount ? null : (
    <Box className={className} data-test-id='Price'>
      <S.PriceWrapper
        {...generateDataTestId('productCard', `Price`)}
        data-test-id='PriceWrapper'
        onClick={(event) => onPriceClick(event)}
      >
        <BasePrice
          data-test-id='PriceBasePrice'
          formatPriceOpts={formatPriceOpts}
          price={price}
          priceUndiscounted={priceUndiscounted}
          size={size}
        />
        {!retailerContext && (
          <S.Icon
            alt='infoBadge'
            data-test-id='PriceIcon'
            height={size === 'm' ? 18 : 14}
            onClick={showPriceModal}
            src={infoBadge}
            width={size === 'm' ? 18 : 14}
          />
        )}
      </S.PriceWrapper>
    </Box>
  );
};

Price.displayName = 'Price';
